import React, { useEffect, useMemo, useState } from "react";

import {
  FiSearch,
  FiUser,
  FiPhone,
  FiMapPin,
  FiPackage,
  FiCheckCircle,
  FiRotateCcw,
  FiClock,
  FiPercent,
  FiChevronDown,
} from "react-icons/fi";

import {
  getRepresentatives,
  getRepresentativeDeliveryStats,
} from "./RepresentativeCommissionService";

const RepresentativeDeliveryStats = () => {
  const [representatives, setRepresentatives] = useState([]);

  const [selectedRepresentative, setSelectedRepresentative] =
    useState(null);

  const [searchName, setSearchName] = useState("");

  const [representative, setRepresentative] = useState(null);

  const [loadingRepresentatives, setLoadingRepresentatives] =
    useState(false);

  const [loadingStats, setLoadingStats] = useState(false);

  const [error, setError] = useState("");

  const [showDropdown, setShowDropdown] = useState(false);

  /* =========================================================
     GET ALL REPRESENTATIVES
  ========================================================= */

  useEffect(() => {
    const loadRepresentatives = async () => {
      try {
        setLoadingRepresentatives(true);
        setError("");

        const response = await getRepresentatives();

        const data = response?.data ?? response;

        setRepresentatives(
          Array.isArray(data) ? data : []
        );
      } catch (error) {
        console.error(error);

        setError(
          error?.response?.data?.message ||
            "حدث خطأ أثناء جلب قائمة المندوبين"
        );
      } finally {
        setLoadingRepresentatives(false);
      }
    };

    loadRepresentatives();
  }, []);

  /* =========================================================
     FILTER REPRESENTATIVES
  ========================================================= */

  const filteredRepresentatives = useMemo(() => {
    const value = searchName.trim().toLowerCase();

    if (!value) {
      return representatives;
    }

    return representatives.filter((item) =>
      item.fullName
        ?.toLowerCase()
        .includes(value)
    );
  }, [representatives, searchName]);

  /* =========================================================
     SELECT REPRESENTATIVE
  ========================================================= */

  const handleSelectRepresentative = async (item) => {
    try {
      setSelectedRepresentative(item);

      setSearchName(item.fullName);

      setShowDropdown(false);

      setRepresentative(null);

      setLoadingStats(true);

      setError("");

      /*
        هنا بالظبط بنبعت اسم المندوب
      */

      const response =
        await getRepresentativeDeliveryStats(
          item.fullName
        );

      const data = response?.data ?? response;

      if (!data) {
        setError("لم يتم العثور على بيانات للمندوب");
        return;
      }

      setRepresentative(data);
    } catch (error) {
      console.error(error);

      setRepresentative(null);

      setError(
        error?.response?.data?.message ||
          "حدث خطأ أثناء جلب إحصائيات المندوب"
      );
    } finally {
      setLoadingStats(false);
    }
  };

  /* =========================================================
     SEARCH INPUT
  ========================================================= */

  const handleSearchChange = (event) => {
    const value = event.target.value;

    setSearchName(value);

    setSelectedRepresentative(null);

    setRepresentative(null);

    setShowDropdown(true);

    setError("");
  };

  /* =========================================================
     RATE CLASS
  ========================================================= */

  const getRateClass = (rate) => {
    if (rate >= 80) return "rate-high";

    if (rate >= 60) return "rate-medium";

    return "rate-low";
  };

  return (
    <div className="delivery-stats-section">

      {/* =====================================================
          SEARCH CARD
      ===================================================== */}

      <div className="search-card">

        <div className="section-title">

          <div className="section-title-icon">
            <FiSearch size={18} />
          </div>

          <div>
            <h3>البحث عن مندوب</h3>

            <p>
              اختر اسم المندوب لعرض إحصائيات التسليم
            </p>
          </div>

        </div>

        {/* ===================================================
            REPRESENTATIVE SELECT
        =================================================== */}

        <div className="representative-select-wrapper">

          <label>
            اسم المندوب
          </label>

          <div className="representative-select">

            <div className="representative-select-input">

              <FiUser size={17} />

              <input
                type="text"
                value={searchName}
                onChange={handleSearchChange}
                onFocus={() => setShowDropdown(true)}
                placeholder={
                  loadingRepresentatives
                    ? "جاري تحميل المندوبين..."
                    : "ابحث عن اسم المندوب..."
                }
                disabled={loadingRepresentatives}
              />

              <FiChevronDown
                size={17}
                className={
                  showDropdown
                    ? "select-arrow-open"
                    : ""
                }
                onClick={() =>
                  setShowDropdown(
                    (previous) => !previous
                  )
                }
              />

            </div>

            {/* =================================================
                DROPDOWN
            ================================================= */}

            {showDropdown &&
              !loadingRepresentatives && (
                <div className="representative-dropdown">

                  {filteredRepresentatives.length > 0 ? (
                    filteredRepresentatives.map(
                      (item) => (
                        <button
                          type="button"
                          key={item.id}
                          className={`representative-option ${
                            selectedRepresentative?.id ===
                            item.id
                              ? "selected"
                              : ""
                          }`}
                          onClick={() =>
                            handleSelectRepresentative(
                              item
                            )
                          }
                        >

                          <div className="option-user-icon">
                            <FiUser size={15} />
                          </div>

                          <div className="option-info">

                            <strong>
                              {item.fullName}
                            </strong>

                            <span>
                              {item.phoneNumber ||
                                "-"}
                              {" • "}
                              {item.governorate?.name ||
                                "-"}
                            </span>

                          </div>

                        </button>
                      )
                    )
                  ) : (
                    <div className="no-representatives">
                      لا يوجد مندوب بهذا الاسم
                    </div>
                  )}

                </div>
              )}

          </div>

        </div>

        {/* ===================================================
            LOADING
        =================================================== */}

        {loadingStats && (
          <div className="stats-loading">
            جاري جلب إحصائيات المندوب...
          </div>
        )}

        {/* ===================================================
            ERROR
        =================================================== */}

        {error && (
          <div className="commission-error">
            {error}
          </div>
        )}

      </div>

      {/* =====================================================
          RESULT
      ===================================================== */}

      {representative && !loadingStats && (
        <div className="representative-result-card">

          {/* =================================================
              REPRESENTATIVE INFO
          ================================================= */}

          <div className="representative-result-header">

            <div className="representative-avatar">
              <FiUser size={23} />
            </div>

            <div>

              <h3>
                {representative.representativeName}
              </h3>

             

            </div>

          </div>

          {/* =================================================
              BASIC INFO
          ================================================= */}

          <div className="representative-basic-info">

            <div className="info-item">

              <FiPhone size={17} />

              <div>

                <span>
                  رقم الهاتف
                </span>

                <strong>
                  {representative.phoneNumber ||
                    "-"}
                </strong>

              </div>

            </div>

            <div className="info-item">

              <FiMapPin size={17} />

              <div>

                <span>
                  المحافظة
                </span>

                <strong>
                  {representative.governorateName ||
                    "-"}
                </strong>

              </div>

            </div>

          </div>

          {/* =================================================
              STATS
          ================================================= */}

          <div className="representative-stat-grid">

            <div className="stat-box">

              <div className="stat-box-icon">
                <FiPackage size={18} />
              </div>

              <div>

                <span>
                  إجمالي الطلبات
                </span>

                <strong>
                  {representative.totalOrders ??
                    0}
                </strong>

              </div>

            </div>

            <div className="stat-box">

              <div className="stat-box-icon">
                <FiCheckCircle size={18} />
              </div>

              <div>

                <span>
                  الطلبات المسلمة
                </span>

                <strong>
                  {representative.deliveredOrders ??
                    0}
                </strong>

              </div>

            </div>

            <div className="stat-box">

              <div className="stat-box-icon">
                <FiRotateCcw size={18} />
              </div>

              <div>

                <span>
                  الطلبات المرتجعة
                </span>

                <strong>
                  {representative.returnedOrders ??
                    0}
                </strong>

              </div>

            </div>

            <div className="stat-box">

              <div className="stat-box-icon">
                <FiClock size={18} />
              </div>

              <div>

                <span>
                  الطلبات المعلقة
                </span>

                <strong>
                  {representative.pendingOrders ??
                    0}
                </strong>

              </div>

            </div>

          </div>

          {/* =================================================
              DELIVERY RATE
          ================================================= */}

          <div className="delivery-rate-section">

            <div className="delivery-rate-title">

              <div>

                <span>
                  نسبة التسليم
                </span>

                <strong>
                  {representative.deliveryRate ??
                    0}
                  %
                </strong>

              </div>

              <div className="delivery-rate-icon">
                <FiPercent size={20} />
              </div>

            </div>

            <div className="delivery-rate-progress">

              <div
                className={`delivery-rate-progress-bar ${getRateClass(
                  representative.deliveryRate
                )}`}
                style={{
                  width: `${Math.min(
                    Math.max(
                      Number(
                        representative.deliveryRate
                      ) || 0,
                      0
                    ),
                    100
                  )}%`,
                }}
              />

            </div>

          </div>

        </div>
      )}

    </div>
  );
};

export default RepresentativeDeliveryStats;